import { useMemo } from 'react'
import Chart from 'react-apexcharts'
import { Card, CardBody, Spinner } from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const ComparisonChart = ({ summaryData, isLoading }) => {
  const chartData = useMemo(() => {
    const data = summaryData?.data || summaryData
    const purchaseMonths = data?.purchase?.months
    const salesMonths = data?.sales?.months

    const isYearly = Array.isArray(purchaseMonths) || Array.isArray(salesMonths)

    if (isYearly) {
      const pMonths = purchaseMonths || []
      const sMonths = salesMonths || []

      if (pMonths.length === 0 && sMonths.length === 0) {
        return { isYearly: true, hasData: false, categories: [], buyValues: [], sellValues: [] }
      }

      // 12 months array (Jan to Des)
      const defaultMonths = [
        'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
        'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
      ]

      const categories = []
      const buyValues = []
      const sellValues = []

      for (let m = 1; m <= 12; m++) {
        const p = pMonths.find((item) => item.month === m)
        const s = sMonths.find((item) => item.month === m)

        const buy = Number(p?.totalAfterVAT ?? p?.totalValue ?? 0)
        const sell = Number(s?.totalAfterVAT ?? s?.totalValue ?? 0)
        if (buy === 0 && sell === 0) continue
        categories.push(defaultMonths[m - 1])
        buyValues.push(buy)
        sellValues.push(sell)
      }

      const hasData = buyValues.some((v) => v !== 0) || sellValues.some((v) => v !== 0)

      return {
        isYearly: true,
        hasData,
        categories,
        buyValues,
        sellValues,
      }
    }

    // Monthly view (per commodity)
    const purchaseItems = data?.purchase?.items || []
    const salesItems = data?.sales?.items || []

    // Collect all unique commodity names
    const allNames = new Set()
    purchaseItems.forEach((i) => allNames.add(i.name))
    salesItems.forEach((i) => allNames.add(i.name))

    if (allNames.size === 0) {
      return { isYearly: false, hasData: false, categories: [], buyValues: [], sellValues: [] }
    }

    // Build a map for quick lookup
    const buyMap = {}
    purchaseItems.forEach((i) => {
      buyMap[i.name] = i.totalAfterVAT ?? i.totalValue ?? 0
    })

    const sellMap = {}
    salesItems.forEach((i) => {
      sellMap[i.name] = i.totalAfterVAT ?? i.totalValue ?? 0
    })

    // Sort by total combined value descending, take top 10
    const sorted = Array.from(allNames)
      .map((name) => ({
        name,
        buy: buyMap[name] || 0,
        sell: sellMap[name] || 0,
        total: (buyMap[name] || 0) + (sellMap[name] || 0),
      }))
      .filter((item) => item.buy !== 0 || item.sell !== 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)

    const hasData = sorted.some((c) => c.buy !== 0 || c.sell !== 0)

    return {
      isYearly: false,
      hasData,
      categories: sorted.map((c) => c.name),
      buyValues: sorted.map((c) => c.buy),
      sellValues: sorted.map((c) => c.sell),
    }
  }, [summaryData])

  const options = {
    chart: {
      type: 'bar',
      height: 380,
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Inter, sans-serif',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          fontSize: '11px',
          colors: '#667085',
        },
        rotate: chartData.isYearly ? 0 : -45,
        rotateAlways: !chartData.isYearly && chartData.categories.length > 5,
        trim: true,
        maxHeight: 80,
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => {
          if (val >= 1_000_000_000) {
            return `Rp${newFormatThousandSeparator(val / 1_000_000_000, 1)}M`
          }
          if (val >= 1_000_000) {
            return `Rp${newFormatThousandSeparator(val / 1_000_000, 1)}Jt`
          }
          if (val >= 1_000) {
            return `Rp${newFormatThousandSeparator(val / 1_000, 1)}Rb`
          }
          return `Rp${newFormatThousandSeparator(val, 0)}`
        },
        style: {
          fontSize: '11px',
          colors: '#667085',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#D92D20', '#039855'],
    tooltip: {
      y: {
        formatter: (val) => `Rp${newFormatThousandSeparator(val, 0)}`,
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '13px',
      fontWeight: 500,
      markers: {
        width: 10,
        height: 10,
        radius: 3,
      },
    },
    grid: {
      borderColor: '#F2F4F7',
      strokeDashArray: 4,
    },
  }

  const series = [
    {
      name: 'Pembelian',
      data: chartData.buyValues,
    },
    {
      name: 'Penjualan',
      data: chartData.sellValues,
    },
  ]

  if (isLoading) {
    return (
      <Card
        className="mb-3"
        style={{ borderRadius: '12px', border: '1px solid #EAECF0' }}
      >
        <CardBody
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 300 }}
        >
          <Spinner color="primary" />
        </CardBody>
      </Card>
    )
  }

  if (chartData.categories.length === 0 || !chartData.hasData) {
    return (
      <Card
        className="mb-3"
        style={{ borderRadius: '12px', border: '1px solid #EAECF0' }}
      >
        <CardBody
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 200 }}
        >
          <span className="text-muted">
            Tidak ada data untuk ditampilkan pada periode ini
          </span>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card
      className="mb-3"
      style={{ borderRadius: '12px', border: '1px solid #EAECF0' }}
    >
      <CardBody>
        <h6
          className="fw-semibold mb-3"
          style={{ color: '#344054', fontSize: '0.95rem' }}
        >
          {chartData.isYearly
            ? 'Perbandingan Pembelian vs Penjualan per Bulan'
            : 'Perbandingan Pembelian vs Penjualan per Barang'}
        </h6>
        <Chart options={options} series={series} type="bar" height={380} />
      </CardBody>
    </Card>
  )
}

export default ComparisonChart
